// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditEvent, AuditEventTargetObjectTypeName } from '@/audit/api/types';
import { Link } from '@/components/link';
import { Typography } from '@mui/material';

interface Props {
  context: AuditEvent['context'];
}

interface EventLink {
  to: string;
  paramKey?: string;
  searchKey?: string;
}

const EVENT_LINK_CONFIG: Partial<
  Record<AuditEventTargetObjectTypeName, EventLink>
> = {
  Asset: { to: '/assets/$assetKey', paramKey: 'assetKey' },
  Customer: { to: '/customers', searchKey: 'customer' },
  Location: { to: '/locations', searchKey: 'location' },
  Project: { to: '/projects/$projectKey', paramKey: 'projectKey' },
  Item: { to: '/catalog/items/$itemKey', paramKey: 'itemKey' },
  Bundle: { to: '/catalog/bundles/$bundleKey', paramKey: 'bundleKey' },
  Ticket: { to: '/tickets/$ticketKey', paramKey: 'ticketKey' },
  'Purchase order': {
    to: '/purchase-orders/$purchaseOrderKey',
    paramKey: 'purchaseOrderKey',
  },
  'External notification': { to: '/organization' },
};

/**
 * Gets the appropriate link component for an audit event based on its context type
 * @param context - The audit event context containing type and entity information
 * @returns A Link component configured for the specific entity type, or null if no link is applicable
 */
export function AuditEventObjectLink(props: Props) {
  const config = EVENT_LINK_CONFIG[props.context.eventTargetObject.typeName];

  if (!config)
    return (
      <Typography variant="body2" color="text.secondary">
        {props.context.eventTargetObject.name}
      </Typography>
    );

  return (
    <Link
      to={config.to}
      {...(config.searchKey && {
        search: { [config.searchKey]: props.context.eventTargetObject.key },
      })}
      {...(config.paramKey && {
        params: { [config.paramKey]: props.context.eventTargetObject.key },
      })}
      variant="body2"
    >
      {props.context.eventTargetObject.name}
    </Link>
  );
}
